import { getAccess } from '../../services/UserService';
import { LEGACY_EXECUTION_API_VERSION, normalizeApiVersion } from '../../services/ApiVersionService';

/**
 * Shared helpers for the persisted Customization definition JSON produced by
 * AccentQueryShapingAssistantDlg.buildCustomizationDefinition():
 * { profile, apiVersion, queries: [{ name, query, resultBinding, resultShape,
 *   splitByColumn }], outputs: [{ name, script }], templateValues, sessionOptions }.
 */

export const parseCustomizationDefinition = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Assistant dialog data props for a stored definition; falls back to the launcher's
 * blank-session defaults when no definition is available (new artifact).
 */
export const definitionToAssistantProps = (definition, fallback = {}) => {
  const def = parseCustomizationDefinition(definition);
  if (!def) return fallback;

  const queries = Array.isArray(def.queries) && def.queries.length > 0
    ? def.queries.map((q) => ({
      name: (q?.name ?? '').toString(),
      query: (q?.query ?? '').toString(),
      resultBinding: (q?.resultBinding ?? '').toString() || undefined,
      resultShape: q?.resultShape ?? undefined,
      splitByColumn: (q?.splitByColumn ?? '').toString(),
    }))
    : fallback.existingQueries;

  const outputs = Array.isArray(def.outputs) && def.outputs.length > 0
    ? def.outputs.map((o) => ({
      name: (o?.name ?? '').toString(),
      script: (o?.script ?? '').toString(),
    }))
    : fallback.existingOutputs;

  return {
    existingQueries: queries,
    existingOutputs: outputs,
    // The version the stored queries were authored for; the assistant keeps it until a
    // query changes (see AccentQueryShapingAssistantDlg). Absent on legacy definitions.
    existingApiVersion: normalizeApiVersion(def.apiVersion, LEGACY_EXECUTION_API_VERSION),
    initialTemplateValues: def.templateValues && typeof def.templateValues === 'object'
      ? def.templateValues
      : fallback.initialTemplateValues,
    initialSessionOptions: def.sessionOptions && typeof def.sessionOptions === 'object'
      ? def.sessionOptions
      : fallback.initialSessionOptions,
    // The design references the definition declares (CustomizationDependentAssets.Member); the dialog's References row.
    initialDependentAssets: Array.isArray(def.dependentAssets) ? def.dependentAssets : fallback.initialDependentAssets,
  };
};

/** Whether the current user may save/manage Customizations (module + feature). */
export const canManageCustomizations = () => {
  const access = getAccess();
  if (!access) return false;
  return !!(access.modules?.().RTL_Customization?.() && access.features?.().FeatureCanManageCustomizations?.());
};
